import { atom } from 'jotai';

import { aggregationsAtom } from '@core/Atoms/Search/AggregationsAtom';
import type { VariantAtom } from '@core/Entities/Search/Atoms/AvailableVariantsAtom.entity';

type Make = {
  key: string;
  displayName: string;
  count?: number;
  make: string;
  modelsPerMake: Model[];
};

type Model = {
  key: string;
  make: string;
  displayName: string;
  count?: number;
  trimsPerModel?: VariantAtom[];
};

type ModelVariant = Omit<Make, 'modelsPerMake'>;

type ModelsAtom = {
  key: string;
  models: Model[];
};

type VariantsAtom = {
  key: string;
  variants: ModelVariant[];
};

export type NormalizedMakeAtom = Record<string, Make>;

export type NormalizedModelAtom = Record<string, Model>;

const normalizedMakeAtom = atom(get => {
  const { makes = [], models = [] } = get(aggregationsAtom);

  const modelsByMake = models.reduce(
    (acc, model) => {
      if (acc[model.make]) {
        acc[model.make].models.push(model as any);
      } else {
        acc[model.make] = {
          key: model.make,
          models: [model as any],
        };
      }

      return acc;
    },
    {} as Record<string, ModelsAtom>,
  );

  return makes.reduce((acc, make) => {
    if (acc[make.key]) {
      acc[make.key].count = make.count;
    } else {
      acc[make.key] = {
        ...make,
        modelsPerMake: modelsByMake[make.key]?.models || [],
      };
    }

    return acc;
  }, {} as NormalizedMakeAtom);
});

const normalizedModelAtom = atom(get => {
  const { models = [], variants } = get(aggregationsAtom);

  const trimsPerModel = variants.reduce(
    (acc, variant) => {
      if (acc[variant.model]) {
        acc[variant.model].variants.push(variant);
      } else {
        acc[variant.model] = {
          key: variant.model,
          variants: [variant],
        };
      }
      return acc;
    },
    {} as Record<string, VariantsAtom>,
  );

  return models.reduce((acc, model) => {
    if (acc[model.key]) {
      acc[model.key].count = model.count;
    } else {
      acc[model.key] = {
        ...model,
        trimsPerModel: trimsPerModel[model.key]?.variants || [],
      };
    }

    return acc;
  }, {} as NormalizedModelAtom);
});

export { normalizedMakeAtom, normalizedModelAtom };
export type { Model, ModelVariant };
